import { Injectable } from '@angular/core';
import { localStorageEnabled } from '@callrail/looky/util';
import { CookieService } from 'ngx-cookie-service';

import { VerticalConfig } from '../../interfaces/vertical-config.interface';

export interface VerticalOverride {
  vertical: string;
  vertical_config: VerticalConfig;
}

// Superadmin dev tooling for verticalization, driven from Backstage. Two independent
// switches:
//
//   • "View as vertical" override — the sa_vertical_override cookie lets a superadmin
//     preview the app through another vertical's lens. It stores the chosen vertical
//     key plus that vertical's terminology (vertical_config), fetched from the
//     superadmin-only backend endpoint. On context init the FE inserts the stored
//     vertical_config into the agency and company context, so a reload re-applies the
//     lens. 24h, client-side only — the backend never reads or manages it.
//
//   • "Highlight vertical labels" reveal — the sa_vertical_label_reveal localStorage
//     flag drives applyLabelReveal(), which wraps every vertical term in guillemets
//     («like this») so it's obvious which on-screen copy is vertical-specific. Works
//     on both a real backend vertical and a previewed override; independent of the
//     override. Persistence lifetime doesn't matter for a diagnostic, so localStorage
//     beats managing another cookie.
@Injectable({
  providedIn: 'root',
})
export class VerticalDevService {
  private static readonly OVERRIDE_COOKIE = 'sa_vertical_override';
  private static readonly LABEL_REVEAL_KEY = 'sa_vertical_label_reveal';

  constructor(private cookieService: CookieService) {}

  // --- "View as vertical" override ---

  public getOverride(): VerticalOverride | null {
    if (!this.cookieService.check(VerticalDevService.OVERRIDE_COOKIE)) {
      return null;
    }
    try {
      return JSON.parse(
        this.cookieService.get(VerticalDevService.OVERRIDE_COOKIE)
      );
    } catch {
      return null;
    }
  }

  public hasOverride(): boolean {
    return this.cookieService.check(VerticalDevService.OVERRIDE_COOKIE);
  }

  // Add cookie that will expire in 24hrs
  public setOverride(vertical: string, verticalConfig: VerticalConfig): void {
    const payload: VerticalOverride = {
      vertical,
      vertical_config: verticalConfig,
    };
    this.cookieService.set(
      VerticalDevService.OVERRIDE_COOKIE,
      JSON.stringify(payload),
      new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
      '/'
    );
  }

  public clearOverride(): void {
    this.cookieService.delete(VerticalDevService.OVERRIDE_COOKIE, '/');
  }

  // --- "Highlight vertical labels" reveal ---

  public isLabelRevealEnabled(): boolean {
    return (
      localStorageEnabled() &&
      localStorage.getItem(VerticalDevService.LABEL_REVEAL_KEY) === 'true'
    );
  }

  public setLabelRevealEnabled(enabled: boolean): void {
    if (!localStorageEnabled()) {
      return;
    }
    if (enabled) {
      localStorage.setItem(VerticalDevService.LABEL_REVEAL_KEY, 'true');
    } else {
      localStorage.removeItem(VerticalDevService.LABEL_REVEAL_KEY);
    }
  }

  /**
   * When the reveal flag is on, wrap the agency's and company's vertical terms in
   * guillemets («like this») in place, so it's obvious which on-screen copy is
   * vertical-specific. Called from CurrentContextService during init, before the
   * agency/company subjects are built. No-op when the flag is off. Works on whatever
   * config is in effect — a real backend vertical or the previewed override.
   */
  public applyLabelReveal(
    agency?: { vertical_config?: VerticalConfig },
    company?: { vertical_config?: VerticalConfig }
  ): void {
    if (!this.isLabelRevealEnabled()) {
      return;
    }
    if (agency?.vertical_config) {
      agency.vertical_config = this.markConfigTerms(agency.vertical_config);
    }
    if (company?.vertical_config) {
      company.vertical_config = this.markConfigTerms(company.vertical_config);
    }
  }

  private markConfigTerms(config: VerticalConfig): VerticalConfig {
    const marked = { ...config } as Record<string, unknown>;
    for (const [key, value] of Object.entries(marked)) {
      // Only swappable string terms; skip display_name (the vertical's own name,
      // not swapped UI copy) and non-string fields (e.g. call_flow_menu_options).
      // Guard against double-wrapping in case this runs more than once.
      if (
        key !== 'display_name' &&
        typeof value === 'string' &&
        value &&
        !value.startsWith('«')
      ) {
        marked[key] = `«${value}»`;
      }
    }
    return marked as VerticalConfig;
  }
}
